import { EducationalLevelEnum } from '@utils/enum/educational-level.enum';

/**
 * Credential-tier utilities used by skills-match fit scoring.
 *
 * Two enums need to be reconciled onto a common ordinal scale:
 *   - The user's planned credential (Skills Survey education form) — 12 values
 *     from `EducationalLevelEnum`.
 *   - The role's typical required credential — 5 string values returned by
 *     PathBuilder's `typicalEducationLevelGroup` field on the occupation.
 *
 * Common scale (0-5):
 *   0 = LESS_THAN_HIGH_SCHOOL (sentinel — hides everything in the corpus,
 *       which is HS-or-above)
 *   1 = HIGH_SCHOOL (incl. POST_SECONDARY_CERTIFICATE, SOME_COLLEGE_COURSES)
 *   2 = ASSOCIATES_DEGREE
 *   3 = BACHELORS_DEGREE (incl. POST_BACCALAUREATE_CERTIFICATE)
 *   4 = MASTERS_DEGREE (incl. POST_MASTERS_CERTIFICATE)
 *   5 = DOCTORATE (incl. FIRST_PROFESSIONAL_DEGREE per BLS/O*NET grouping,
 *       and POST_DOCTORAL_TRAINING)
 *
 * "Round down" rule: an intermediate credential (e.g. POST_BACCALAUREATE_CERTIFICATE)
 * is treated as the tier of the most recent completed degree. A user with a
 * BA + post-bacc certificate is "at the BA tier" — the certificate doesn't
 * lift them up to MA.
 *
 * BLS/O*NET groups professional degrees (JD, MD, DDS, etc.) with research
 * doctorates in a single "Doctoral or professional degree" category for
 * occupation classifications, so FIRST_PROFESSIONAL_DEGREE maps to tier 5
 * alongside DOCTORAL_DEGREE.
 */

const USER_ENUM_TO_TIER: Record<EducationalLevelEnum, number> = {
  [EducationalLevelEnum.LessThanHighSchool]: 0,
  [EducationalLevelEnum.HighSchool]: 1,
  [EducationalLevelEnum.PostSecondaryCertificate]: 1,
  [EducationalLevelEnum.SomeCollegeCourses]: 1,
  [EducationalLevelEnum.AssociatesDegree]: 2,
  [EducationalLevelEnum.BachelorsDegree]: 3,
  [EducationalLevelEnum.PostBaccalaureateCertificate]: 3,
  [EducationalLevelEnum.MastersDegree]: 4,
  [EducationalLevelEnum.PostMastersCertificate]: 4,
  [EducationalLevelEnum.FirstProfessionalDegree]: 5,
  [EducationalLevelEnum.DoctoralDegree]: 5,
  [EducationalLevelEnum.PostDoctoralTraining]: 5
};

const ROLE_STRING_TO_TIER: Record<string, number> = {
  'High School Diploma': 1,
  "Associate's Degree": 2,
  "Bachelor's Degree": 3,
  "Master's Degree": 4,
  "Doctorate's Degree": 5
};

interface MajorWithEducationLevel {
  educationLevel?: EducationalLevelEnum | null;
}

/**
 * Returns the highest tier across the user's education form entries.
 * The user may list multiple credentials (e.g. an AS and a BA); the highest
 * one is the relevant constraint for matching.
 *
 * Returns `null` when no usable education info is present, signaling to
 * callers that credential-aware scoring should be skipped and skill-only
 * matching should be used instead.
 */
export const userPlannedCredentialTier = (majors: MajorWithEducationLevel[] | null | undefined): number | null => {
  if (!majors?.length) {
    return null;
  }

  const tiers = majors
    .map(m => m?.educationLevel)
    .filter((level): level is EducationalLevelEnum => level != null && level in USER_ENUM_TO_TIER)
    .map(level => USER_ENUM_TO_TIER[level]);

  if (tiers.length === 0) {
    return null;
  }

  return Math.max(...tiers);
};

/**
 * Maps the role's `typicalEducationLevelGroup` string to the same 1-5 tier
 * scale. Returns null for missing or unrecognized values; callers should
 * fall back to skill-only matching for that role (don't credential-cap
 * something we can't classify).
 */
export const roleCredentialTier = (typicalEducationLevelGroup: string | null | undefined): number | null => {
  if (!typicalEducationLevelGroup) {
    return null;
  }
  return ROLE_STRING_TO_TIER[typicalEducationLevelGroup] ?? null;
};

export type CredentialGapDirection = 'exact' | 'over' | 'under';

export interface CredentialGap {
  delta: number;
  direction: CredentialGapDirection;
}

/**
 * Compares user planned tier vs role required tier (both 1-5).
 *
 *   userTier > roleTier  → direction 'over',  delta = userTier - roleTier
 *   userTier < roleTier  → direction 'under', delta = roleTier - userTier
 *   userTier == roleTier → direction 'exact', delta = 0
 */
export const credentialGap = (userTier: number, roleTier: number): CredentialGap => {
  if (userTier === roleTier) {
    return { delta: 0, direction: 'exact' };
  }
  if (userTier > roleTier) {
    return { delta: userTier - roleTier, direction: 'over' };
  }
  return { delta: roleTier - userTier, direction: 'under' };
};
